/**
 * File Validation Service
 * Validates files against provider-specific limits before upload
 * Also validates model capabilities (e.g., vision support for images)
 *
 * Standardized backend error contract (Change E):
 *   { success: false, error_code: <FileError>, message: <EN copy>, details: {...} }
 *
 * Use ``parseUploadError`` to read the contract off an Axios error / Response,
 * and ``showUploadErrorModal`` to render the friendly Ant Design modal that
 * matches Change A's UX.
 */

import api from '@services/api';
import { cachedGet } from '@services/cachedGet';
import { Modal } from 'antd';
import i18n from 'i18next';

/** Loose translator type — accepts both the i18next ``TFunction`` and the
 *  thinner ``t`` returned by ``useLanguage``. */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface FileLimits {
  max_size_mb: number | null;
  max_count: number | null;
  allowed_extensions: string[];
  supported_types?: string[];
}

interface ProviderLimits {
  images: FileLimits;
  docs: FileLimits;
  text: FileLimits;
  spreadsheets?: FileLimits;
  presentations?: FileLimits;
  fonts?: FileLimits;
  audio?: FileLimits;
  video?: FileLimits;
}

type FileCategory = 'images' | 'docs' | 'text' | 'spreadsheets' | 'presentations' | 'fonts';

interface GlobalLimits {
  max_files_per_message: number;
  max_files_per_conversation?: number;
  max_total_size_mb: number;
  blocked_extensions?: string[];
  blocked_mime_types?: string[];
}

interface AllLimits {
  global?: GlobalLimits;
  providers: Record<string, ProviderLimits>;
}

interface ModelCapabilities {
  supports_vision: boolean;
  name: string;
}

let cachedLimits: AllLimits | null = null;
let cachedModelCapabilities: Record<string, ModelCapabilities> | null = null;

// Global file limit (fallback if config not loaded)
const DEFAULT_MAX_FILES = 10;

/**
 * Fetch all limits from backend (cached)
 */
export async function getAllLimits(): Promise<AllLimits> {
  if (cachedLimits) return cachedLimits;

  try {
    // cachedGet, not api.get: the `cachedLimits` guard above only helps AFTER
    // the first call resolves. Four panels mounting at once all see it empty
    // and all fetch. Single-flight collapses them into one request.
    const data = await cachedGet<any>('/files/limits', 5 * 60_000);
    cachedLimits = {
      global: data.global,
      providers: data.providers
    };
    return cachedLimits;
  } catch (error) {
    console.error('[fileValidationService] Failed to fetch limits:', error);
    return { providers: {} };
  }
}

/**
 * Fetch provider limits from backend (cached)
 */
export async function getProviderLimits(): Promise<Record<string, ProviderLimits>> {
  const allLimits = await getAllLimits();
  return allLimits.providers || {};
}

/**
 * Get global limits
 */
export async function getGlobalLimits(): Promise<GlobalLimits> {
  const allLimits = await getAllLimits();
  return allLimits.global || { max_files_per_message: DEFAULT_MAX_FILES, max_total_size_mb: 100 };
}

/**
 * Determine file category based on extension
 */
function getFileCategory(file: File): FileCategory {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';

  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return 'images';
  }
  if (['pdf', 'doc', 'docx', 'pages'].includes(ext)) {
    return 'docs';
  }
  if (['xlsx', 'xlsm', 'xls', 'csv', 'tsv', 'numbers'].includes(ext)) {
    // Every grid is a spreadsheet — including delimited text, legacy .xls and
    // Apple Numbers, which the Excel tools now read (backend mirrors this in
    // file_validation.get_file_category; the extension lists come from
    // /files/limits, so only the CATEGORY mapping lives here).
    return 'spreadsheets';
  }
  if (['pptx', 'key'].includes(ext)) {
    return 'presentations';
  }
  if (['ttf', 'otf', 'woff', 'woff2'].includes(ext)) {
    // Brand material for the brand-book agent: tool-only, never prose.
    // Mirrors the backend's FONT_EXTENSIONS.
    return 'fonts';
  }
  // Everything else is text (txt, json, md, etc.)
  return 'text';
}

/**
 * Check if a file is on the deferred-types blocklist (Excel / CSV / PPTX).
 * These match ``global.blocked_extensions`` / ``global.blocked_mime_types``
 * in ``config/ai_model_limits.json`` — the source of truth lives on the
 * backend; this helper reads the cached snapshot from /files/limits.
 */
export async function isFileTypeBlocked(file: File): Promise<boolean> {
  const limits = await getGlobalLimits();
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  const blockedExts = limits.blocked_extensions ?? [];
  const blockedMimes = limits.blocked_mime_types ?? [];
  if (blockedExts.includes(ext)) return true;
  if (file.type && blockedMimes.includes(file.type)) return true;
  return false;
}

/**
 * Build the comma-separated string for ``<input type="file" accept="...">``
 * from the active provider's allow-list minus the blocked extensions.
 * The OS file picker uses this to filter what the user can pick.
 */
export async function getAcceptAttribute(provider: string | null): Promise<string> {
  if (!provider) return '';
  const all = await getAllLimits();
  const providerLimits = all.providers[provider];
  if (!providerLimits) return '';

  const globalLimits = all.global ?? { max_files_per_message: 10, max_total_size_mb: 100 };
  const blocked = new Set(globalLimits.blocked_extensions ?? []);

  const exts = new Set<string>();
  (['images', 'docs', 'text', 'spreadsheets', 'presentations', 'fonts', 'audio', 'video'] as const).forEach((cat) => {
    const c = providerLimits[cat];
    if (!c) return;
    (c.allowed_extensions ?? []).forEach((e: string) => {
      if (!blocked.has(e)) exts.add(e);
    });
  });
  return Array.from(exts).join(',');
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
  file: File;
}

/**
 * Validate files against provider limits
 * Returns validation result for each file
 * If limits can't be fetched, allows files through (fail open)
 */
export async function validateFilesForModel(
  files: File[],
  provider: string
): Promise<ValidationResult[]> {
  const t = i18n.t.bind(i18n);
  const limits = await getProviderLimits();

  // If we couldn't fetch limits or provider not found, allow files through
  // Backend will do final validation
  if (!limits || Object.keys(limits).length === 0) {
    console.warn('[fileValidationService] Could not fetch limits, allowing files through');
    return files.map(f => ({ valid: true, file: f }));
  }

  const providerLimits = limits[provider];
  if (!providerLimits) {
    console.warn(`[fileValidationService] No limits for provider ${provider}, allowing files through`);
    return files.map(f => ({ valid: true, file: f }));
  }

  return files.map(file => {
    const category = getFileCategory(file);
    const categoryLimits = providerLimits[category];

    if (!categoryLimits) {
      return { valid: true, file };
    }

    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');

    // Check extension
    if (categoryLimits.allowed_extensions && categoryLimits.allowed_extensions.length > 0) {
      if (!categoryLimits.allowed_extensions.includes(ext)) {
        return {
          valid: false,
          error: t('fileUpload.errors.typeNotSupported', { ext, provider }) +
                 '. ' + t('fileUpload.errors.allowedTypes', { types: categoryLimits.allowed_extensions.join(', ') }),
          file
        };
      }
    }

    // Check size
    const sizeMB = file.size / (1024 * 1024);
    if (categoryLimits.max_size_mb && sizeMB > categoryLimits.max_size_mb) {
      return {
        valid: false,
        error: t('fileUpload.errors.fileTooLarge', {
          fileName: file.name,
          size: sizeMB.toFixed(1),
          provider,
          limit: categoryLimits.max_size_mb
        }),
        file
      };
    }

    return { valid: true, file };
  });
}

/**
 * Get the max file count limit for a category
 */
export function getMaxFileCount(provider: string, category: FileCategory): number | null {
  if (!cachedLimits?.providers) return null;
  const providerLimits = cachedLimits.providers[provider];
  if (!providerLimits) return null;
  return providerLimits[category]?.max_count ?? null;
}

/**
 * Get all limits for display purposes
 */
export function getProviderLimitsSync(): Record<string, ProviderLimits> | null {
  return cachedLimits?.providers ?? null;
}

/**
 * Check if adding new files would exceed count limits
 * Returns error message if limit would be exceeded, null if OK
 */
export async function validateFileCount(
  existingFiles: File[],
  newFiles: File[],
  provider: string
): Promise<{ valid: boolean; error?: string }> {
  const t = i18n.t.bind(i18n);
  const limits = await getProviderLimits();

  if (!limits || Object.keys(limits).length === 0) {
    return { valid: true }; // Fail open
  }

  const providerLimits = limits[provider];
  if (!providerLimits) {
    return { valid: true }; // Fail open
  }

  // Count files by category (existing + new)
  const allFiles = [...existingFiles, ...newFiles];
  const categoryCounts: Record<string, number> = { images: 0, docs: 0, text: 0, spreadsheets: 0, presentations: 0, fonts: 0 };

  for (const file of allFiles) {
    const category = getFileCategory(file);
    categoryCounts[category]++;
  }

  // Check each category against limits
  for (const [category, count] of Object.entries(categoryCounts)) {
    const categoryLimits = providerLimits[category as keyof ProviderLimits];
    if (categoryLimits && 'max_count' in categoryLimits && categoryLimits.max_count !== null) {
      if (count > categoryLimits.max_count) {
        const categoryLabel =
          category === 'images' ? 'images' :
          category === 'docs' ? 'documents' :
          category === 'spreadsheets' ? 'spreadsheets' :
          category === 'presentations' ? 'presentations' :
          category === 'fonts' ? 'font files' :
          'files';
        return {
          valid: false,
          error: t('fileUpload.errors.tooManyFiles', {
            count,
            limit: categoryLimits.max_count,
            category: categoryLabel,
            provider
          })
        };
      }
    }
  }

  return { valid: true };
}

/**
 * Clear cached limits (useful for testing or config changes)
 */
export function clearLimitsCache(): void {
  cachedLimits = null;
  cachedModelCapabilities = null;
}

/**
 * Fetch model capabilities from backend (cached)
 */
export async function getModelCapabilities(): Promise<Record<string, ModelCapabilities>> {
  if (cachedModelCapabilities) return cachedModelCapabilities;

  try {
    const { data } = await api.get('/ai/models/');
    const models = data.models || data;

    cachedModelCapabilities = {};
    models.forEach((model: { id: string; name: string; resolved_model_name?: string; supports_vision?: boolean }) => {
      cachedModelCapabilities![model.id] = {
        supports_vision: model.supports_vision ?? true,
        // The release that would read the image (the tenant's pin), as the
        // picker names it — not the family's name.
        name: model.resolved_model_name || model.name
      };
    });
    return cachedModelCapabilities;
  } catch (error) {
    console.error('[fileValidationService] Failed to fetch model capabilities:', error);
    return {}; // Fail open - let backend validate
  }
}

/**
 * Check if a file is an image based on extension
 */
function isImageFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
}

/**
 * Check if model supports the given file
 * Returns error message if not supported, null if supported
 */
export async function checkModelSupportsFile(
  file: File,
  modelId: string
): Promise<{ supported: boolean; error?: string; modelName?: string }> {
  const t = i18n.t.bind(i18n);

  // Only check images - other file types are always allowed
  if (!isImageFile(file)) {
    return { supported: true };
  }

  const capabilities = await getModelCapabilities();
  const modelCaps = capabilities[modelId];

  // If we can't find the model, fail open (backend will validate)
  if (!modelCaps) {
    return { supported: true };
  }

  if (!modelCaps.supports_vision) {
    return {
      supported: false,
      error: t('fileUpload.errors.imagesNotSupportedByModel', { modelName: modelCaps.name }),
      modelName: modelCaps.name
    };
  }

  return { supported: true };
}

export interface ReadabilityResult {
  readable: boolean;
  error?: string;
  file: File;
}

/**
 * Check if files can be read (not corrupted)
 * Attempts to read first 1KB of each file
 */
export async function checkFilesReadable(files: File[]): Promise<ReadabilityResult[]> {
  const t = i18n.t.bind(i18n);

  return Promise.all(
    files.map(async (file) => {
      try {
        // Try to read first 1KB of file
        const slice = file.slice(0, 1024);
        await slice.arrayBuffer();
        return { readable: true, file };
      } catch {
        return {
          readable: false,
          error: t('fileUpload.errors.cannotRead', { fileName: file.name }),
          file
        };
      }
    })
  );
}

/**
 * File validation status with detailed error information
 */
export interface FileValidationStatus {
  file: File;
  status: 'pending' | 'valid' | 'invalid';
  error?: string;
  errorCode?: 'TYPE_NOT_SUPPORTED' | 'SIZE_TOO_LARGE' | 'NO_VISION' | 'CORRUPTED' | 'TOO_MANY_FILES';
}

/**
 * Validate a single file for a specific model
 * Returns status with error details if invalid
 * Used for real-time visual feedback in ChatInput
 *
 * `provider` is the provider the composer will send with — resolved from the
 * catalogue by `utils/modelProvider.providerOfModel`, never guessed from a
 * family key's spelling. null (an unknown ref) skips the provider's type and
 * size rules; the server checks them against the model the turn runs on.
 */
export async function validateFileForModel(
  file: File,
  modelId: string,
  provider: string | null,
  existingFilesCount: number
): Promise<FileValidationStatus> {
  const t = i18n.t.bind(i18n);
  const globalLimits = await getGlobalLimits();
  const providerLimits = await getProviderLimits();

  // 1. Check global file count limit
  if (existingFilesCount >= globalLimits.max_files_per_message) {
    return {
      file,
      status: 'invalid',
      error: t('fileUpload.errors.tooManyFiles', { limit: globalLimits.max_files_per_message }),
      errorCode: 'TOO_MANY_FILES'
    };
  }

  // 2. Check file readability
  try {
    const slice = file.slice(0, 1024);
    await slice.arrayBuffer();
  } catch {
    return {
      file,
      status: 'invalid',
      error: t('fileUpload.errors.cannotRead', { fileName: file.name }),
      errorCode: 'CORRUPTED'
    };
  }

  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  const category = getFileCategory(file);

  // 3. Check if image and model doesn't support vision
  if (isImageFile(file)) {
    const capabilities = await getModelCapabilities();
    const modelCaps = capabilities[modelId];
    if (modelCaps && !modelCaps.supports_vision) {
      return {
        file,
        status: 'invalid',
        error: t('fileUpload.errors.imagesNotSupportedByModel', { modelName: modelCaps.name }),
        errorCode: 'NO_VISION'
      };
    }
  }

  // 4. Check extension is allowed for this provider
  const limits = provider ? providerLimits[provider] : undefined;
  if (limits) {
    const categoryLimits = limits[category];
    if (categoryLimits?.allowed_extensions && categoryLimits.allowed_extensions.length > 0) {
      if (!categoryLimits.allowed_extensions.includes(ext)) {
        return {
          file,
          status: 'invalid',
          error: t('fileUpload.errors.typeNotSupported', { ext, provider }),
          errorCode: 'TYPE_NOT_SUPPORTED'
        };
      }
    }

    // 5. Check size limits
    const sizeMB = file.size / (1024 * 1024);
    if (categoryLimits?.max_size_mb && sizeMB > categoryLimits.max_size_mb) {
      return {
        file,
        status: 'invalid',
        error: t('fileUpload.errors.fileTooLarge', {
          fileName: file.name,
          size: sizeMB.toFixed(1),
          limit: categoryLimits.max_size_mb
        }),
        errorCode: 'SIZE_TOO_LARGE'
      };
    }
  }

  return { file, status: 'valid' };
}

/* -------------------------------------------------------------------------
 * Standardized backend error contract (Change E + Change A)
 * ------------------------------------------------------------------------- */

/** Every code in MESSAGE_BY_CODE on the backend (`backend/modules/files/errors.py`). */
export type BackendFileErrorCode =
  | 'FILE_TOO_LARGE'
  | 'TOO_MANY_FILES_PER_MESSAGE'
  | 'TOO_MANY_FILES_PER_CONVERSATION'
  | 'TYPE_NOT_SUPPORTED'
  | 'FILE_TYPE_NOT_SUPPORTED_YET'
  | 'FILE_EXTRACTION_FAILED'
  | 'CONVERSATION_FULL'
  | 'CONVERSATION_NEARLY_FULL'
  | 'AUTO_COMPACT_FAILED'
  | 'CLOUD_NOT_CONNECTED'
  | 'CLOUD_FILE_UNAVAILABLE'
  | 'GOOGLE_ACCESS_NEEDED'
  | 'IWORK_LEGACY'
  | 'IWORK_ENCRYPTED'
  | 'IWORK_UNREADABLE';

/** The codes that refuse a FILE — each worded under common.upload.errors.*.
 * The others the contract carries are about the conversation (FULL…) or ask
 * for Google access, and each has its own surface. */
const FILE_REFUSAL_CODES: ReadonlySet<string> = new Set<BackendFileErrorCode>([
  'FILE_TOO_LARGE',
  'TOO_MANY_FILES_PER_MESSAGE',
  'TOO_MANY_FILES_PER_CONVERSATION',
  'TYPE_NOT_SUPPORTED',
  'FILE_TYPE_NOT_SUPPORTED_YET',
  'FILE_EXTRACTION_FAILED',
  'CLOUD_NOT_CONNECTED',
  'CLOUD_FILE_UNAVAILABLE',
  'IWORK_LEGACY',
  'IWORK_ENCRYPTED',
  'IWORK_UNREADABLE',
]);

/** A refused request's body → the file refusal it carries, or null. */
export function fileRefusalFromBody(body: string): UploadError | null {
  let parsed: unknown = null;
  try { parsed = JSON.parse(body); } catch { return null; }
  const refusal = parseUploadError(parsed);
  return refusal && FILE_REFUSAL_CODES.has(refusal.code) ? new UploadError(refusal) : null;
}

export interface UploadErrorPayload {
  code: BackendFileErrorCode;
  message: string;
  details: Record<string, unknown>;
}

export class UploadError extends Error {
  readonly code: BackendFileErrorCode;
  readonly details: Record<string, unknown>;
  constructor(payload: UploadErrorPayload) {
    super(payload.message || payload.code);
    this.name = 'UploadError';
    this.code = payload.code;
    this.details = payload.details ?? {};
  }
}

/**
 * Pull the standardized error contract off an Axios error / Fetch response /
 * already-thrown UploadError. Returns null if the response doesn't carry the
 * contract (caller should fall back to the legacy error path).
 */
export function parseUploadError(err: unknown): UploadErrorPayload | null {
  if (err instanceof UploadError) {
    return { code: err.code, message: err.message, details: err.details };
  }

  // Axios error shape
  const anyErr = err as { response?: { data?: any } } | undefined;
  const data = anyErr?.response?.data;
  if (data && typeof data === 'object' && data.error_code) {
    return {
      code: data.error_code as BackendFileErrorCode,
      message: typeof data.message === 'string' ? data.message : data.error_code,
      details: (data.details as Record<string, unknown>) ?? {},
    };
  }

  // Already-parsed JSON body
  if (
    err &&
    typeof err === 'object' &&
    'error_code' in (err as object) &&
    typeof (err as { error_code: unknown }).error_code === 'string'
  ) {
    const obj = err as { error_code: BackendFileErrorCode; message?: string; details?: Record<string, unknown> };
    return {
      code: obj.error_code,
      message: obj.message ?? obj.error_code,
      details: obj.details ?? {},
    };
  }

  return null;
}

/**
 * Render the friendly Modal.error for a backend file-upload rejection.
 * Title + body + button copy all come from i18n; interpolation values come
 * from the error's `details` payload so the message stays accurate per case.
 *
 * Returns true if a modal was shown (so callers can avoid duplicate toasts);
 * false if the error wasn't a recognized upload-contract failure.
 */
export function showUploadErrorModal(err: unknown, t: TranslateFn): boolean {
  const payload = parseUploadError(err);
  if (!payload) return false;

  const interpolation: Record<string, unknown> = {
    maxFiles:
      payload.details.max_files ??
      payload.details.maxFiles ??
      undefined,
    supportedTypes:
      payload.details.allowed_extensions
        ? (payload.details.allowed_extensions as string[])
            .map((ext) => ext.replace(/^\./, '').toUpperCase())
            .join(', ')
        : undefined,
    fileName: payload.details.file_name,
    sizeMb: payload.details.size_mb,
    limitMb: payload.details.limit_mb,
    app: payload.details.app,
    twin: payload.details.twin,
  };

  Modal.error({
    title: t('common.upload.errors.title'),
    content: t(`common.upload.errors.${payload.code}`, {
      defaultValue: payload.message,
      ...interpolation,
    }),
    okText: t('common.upload.errors.ok'),
    centered: true,
  });
  return true;
}
